import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { getProjects, getSourcePreparationProgress } from '@src/apis/project';
import { COLOR } from '@src/styles/color';
import CommonButton from '@src/new-components/Button';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Typography from '@src/new-components/Typography';
import route from '@src/constants/route';
import theme from '@src/styles/theme';
import { PAGINATION_LIMIT_REQUEST } from '@src/constants';
import {
  EXTRACTION_STATUS,
  FETCH_EXTRACTION_STATUS_INTERVAL,
  FETCH_EXTRACTION_STATUS_INTERVAL_LARGE,
  PPTX_SMALL_SLIDE_THRESHOLD,
  PROJECT_PRODUCT,
} from '@src/constants/project';
import ActionDialog from '@src/new-components/Dialog/ActionDialog';
import CrownImg from '@src/assets/images/crown.png';
import debounce from '@src/utils/debounce';
import { ERROR_CODE } from '@src/errors/code';
import { StyledProjectLayout } from './index.style';
import ProjectHeader from './ProjectHeader';
import ProjectInput from './ProjectInput';
import ProjectList from './ProjectList';
import ProjectOnMobile from './ProjectOnMobile';

const Project = () => {
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [hasAnyProject, setHasAnyProject] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [openPopupProjectLimitReached, setOpenPopupProjectLimitReached] =
    useState(false);
  const [projectLimit, setProjectLimit] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(PAGINATION_LIMIT_REQUEST);
  const [processingProjects, setProcessingProjects] = useState(new Map());
  const pendingProjectIdsRef = useRef(new Set());
  const pendingSlowProjectIdsRef = useRef(new Set());
  const fetchProjectsRef = useRef(null);

  const handleSearchInputChange = (e) => setSearch(e.target.value);

  const fetchProjects = async (hasLoading) => {
    if (hasLoading) setLoading(true);
    const newParams = {
      search: search?.trim(),
      offset: page * rowsPerPage,
      limit: rowsPerPage,
      sort: 'updatedAt_desc',
    };
    const response = await getProjects(newParams);
    if (response && response.status) {
      const projectsResult = response.result.projects;
      const totalResult = response.result?.metadata?.total || 0;
      setProjects(projectsResult);
      if (!search) {
        setHasAnyProject(totalResult > 0);
      }

      setTotal(totalResult);
      setLoading(false);

      const isProjectPending = (p) => {
        const hasPendingProject = [
          PROJECT_PRODUCT.PRESENTATION,
          PROJECT_PRODUCT.DUBBING,
        ].includes(p.product);

        if (hasPendingProject) {
          return (
            p.source?.extraction?.response?.status === EXTRACTION_STATUS.PENDING
          );
        }
        return false;
      };
      const pendingFromServer = projectsResult.filter(
        (p) =>
          isProjectPending(p) &&
          !pendingProjectIdsRef.current.has(p.id) &&
          !pendingSlowProjectIdsRef.current.has(p.id),
      );
      pendingFromServer.forEach((p) => {
        const isLargePresentation =
          p.product === PROJECT_PRODUCT.PRESENTATION &&
          (p.source?.metadata?.slideCount ?? 0) >= PPTX_SMALL_SLIDE_THRESHOLD;
        if (isLargePresentation) {
          pendingSlowProjectIdsRef.current.add(p.id);
        } else {
          pendingProjectIdsRef.current.add(p.id);
        }
        setProcessingProjects((prev) => {
          if (prev.has(p.id)) return prev;
          const next = new Map(prev);
          next.set(p.id, { status: EXTRACTION_STATUS.PENDING });
          return next;
        });
      });
    }
  };

  fetchProjectsRef.current = fetchProjects;

  const handleFetchProgress = async (projectIdsRef) => {
    if (projectIdsRef.current.size === 0) return;

    await Promise.all(
      [...projectIdsRef.current].map(async (projectId) => {
        const progress = await getSourcePreparationProgress(projectId);

        if (progress?.errorCode === ERROR_CODE.PROJECT_NOT_FOUND) {
          projectIdsRef.current.delete(projectId);
          setProcessingProjects((prev) => {
            const next = new Map(prev);
            next.delete(projectId);
            return next;
          });
          return;
        }

        const status = progress?.result?.status;
        const errorMessage = progress?.result?.error?.message;

        if (status === EXTRACTION_STATUS.SUCCESS) {
          projectIdsRef.current.delete(projectId);
          setProcessingProjects((prev) => {
            const next = new Map(prev);
            next.delete(projectId);
            return next;
          });
          fetchProjectsRef.current(false);
        } else if (status === EXTRACTION_STATUS.FAILURE) {
          projectIdsRef.current.delete(projectId);
          setProcessingProjects((prev) => {
            const next = new Map(prev);
            next.set(projectId, {
              status: EXTRACTION_STATUS.FAILURE,
              errorMessage,
            });
            return next;
          });
        }
      }),
    );
  };

  const hasInProgressProjects = [...processingProjects.values()].some(
    (p) => p.status === EXTRACTION_STATUS.PENDING,
  );

  const hasInProgressFastProjects =
    hasInProgressProjects && pendingProjectIdsRef.current.size > 0;
  const hasInProgressSlowProjects =
    hasInProgressProjects && pendingSlowProjectIdsRef.current.size > 0;

  useEffect(() => {
    if (!hasInProgressFastProjects) return undefined;

    const interval = setInterval(
      () => handleFetchProgress(pendingProjectIdsRef),
      FETCH_EXTRACTION_STATUS_INTERVAL,
    );

    return () => clearInterval(interval);
  }, [hasInProgressFastProjects]);

  useEffect(() => {
    if (!hasInProgressSlowProjects) return undefined;

    const interval = setInterval(
      () => handleFetchProgress(pendingSlowProjectIdsRef),
      FETCH_EXTRACTION_STATUS_INTERVAL_LARGE,
    );

    return () => clearInterval(interval);
  }, [hasInProgressSlowProjects]);

  const addProcessingProject = useCallback((projectId, isLarge = false) => {
    if (isLarge) {
      pendingSlowProjectIdsRef.current.add(projectId);
    } else {
      pendingProjectIdsRef.current.add(projectId);
    }
    setProcessingProjects((prev) => {
      const next = new Map(prev);
      next.set(projectId, { status: EXTRACTION_STATUS.PENDING });
      return next;
    });
  }, []);

  const handleOpenPopupProjectLimitReached = (limit) => {
    setProjectLimit(limit);
    setOpenPopupProjectLimitReached(true);
  };

  const handleClosePopupProjectLimitReached = () => {
    setOpenPopupProjectLimitReached(false);
  };

  useEffect(() => {
    debounce(fetchProjects, 500)(search);
  }, [search]);

  useEffect(() => {
    fetchProjects(true);
  }, [page, rowsPerPage]);

  const handleDeleteSuccess = (projectId) => {
    setProjects(projects.filter((project) => project.id !== projectId));
    if (page !== 0) {
      setPage(0);
    } else {
      fetchProjects(false);
    }
  };

  const handleDeleteManySuccess = () => {
    if (page !== 0) {
      setPage(0);
    } else {
      fetchProjects(true);
    }
  };

  if (isMobile) {
    return <ProjectOnMobile />;
  }

  return (
    <StyledProjectLayout>
      <ProjectHeader />
      <ProjectInput
        refetchProjects={fetchProjects}
        handleOpenPopupProjectLimitReached={handleOpenPopupProjectLimitReached}
        addProcessingProject={addProcessingProject}
      />
      <ProjectList
        projects={projects}
        hasAnyProject={hasAnyProject}
        loading={loading}
        handleSearchInputChange={handleSearchInputChange}
        onDeleteSuccess={handleDeleteSuccess}
        onDeleteManySuccess={handleDeleteManySuccess}
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        onChangePage={setPage}
        onChangeRowPage={setRowsPerPage}
        refetchProjects={fetchProjects}
        rowsPerPageOptions={[10, 25]}
        search={search}
        processingProjects={processingProjects}
      />
      <ActionDialog
        open={openPopupProjectLimitReached}
        title={t('projectLimitReached')}
        styleTitle={{
          textAlign: 'center',
        }}
        contentComponent={
          <Typography
            style={{ textAlign: 'center' }}
            variant="body-16-regular"
            color={COLOR.descriptionGray}
            dangerouslySetInnerHTML={{
              __html: t('projectLimitReachedDescription', {
                number: projectLimit,
              }),
            }}
          />
        }
        fullWidth
        image={CrownImg}
        imageContainerSize="100px"
        onClose={handleClosePopupProjectLimitReached}
        width="640px"
        actionComponent={
          <CommonButton
            variant="contained"
            onClick={() => history.push(route.PAYMENT)}
            textProps={{
              variantText: 'body-14-semibold',
              color: COLOR.darkBlue,
            }}
            customStyle={{
              height: '40px',
              borderRadius: '8px',
              padding: '8px 24px',
              width: '146px',
            }}
          >
            {t('upgradeNow')}
          </CommonButton>
        }
      />
    </StyledProjectLayout>
  );
};

export default Project;
